const DAY_NAMES = ['Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays'];

/**
 * Formats a duration given in seconds as a human-readable string, retaining
 * the stored seconds precision. Below an hour renders as "Xm SSs"; at or
 * above an hour renders as "Xh MMm". The trailing unit is zero-padded so
 * columns line up in a table with tabular numerals.
 *
 * @param {number} seconds - The duration in seconds.
 * @returns {string} The formatted duration, e.g. "1h 35m" or "5m 00s".
 */
export function formatDuration(seconds) {
  if (seconds >= 3600) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${String(remainingSeconds).padStart(2, '0')}s`;
}

// The Route Optimization API's own wire form for a duration: the string "3600s",
// not a number. Every value on the configuration page is edited in human form
// and stored in this one, so the detection lives here beside the formatters
// rather than being re-derived by each component that has to tell the two apart.
const WIRE_DURATION = /^(\d+(?:\.\d+)?)s$/;

/**
 * Reads a value in the API's duration wire form.
 *
 * @param {*} value - Any value from the knobs payload.
 * @returns {?number} The duration in seconds, or null if it is not one.
 */
export function durationSeconds(value) {
  if (typeof value !== 'string') return null;

  const match = WIRE_DURATION.exec(value);
  return match ? Number.parseFloat(match[1]) : null;
}

/**
 * Formats one value for display by its own kind rather than its knob's: a
 * duration reads as "1h 35m", anything else as itself. A structured knob's
 * members can differ in kind, so the decision is per value.
 *
 * @param {*} value
 * @returns {string}
 */
export function displayValue(value) {
  const seconds = durationSeconds(value);
  return seconds === null ? String(value) : formatDuration(seconds);
}

/**
 * Whether a structured knob's member holds a GROUP of values rather than one
 * value. Most structured knobs are one level deep (`max_pickup_to_delivery` is
 * an inbound and an outbound), but `vehicle_fixed_cost_by_type` prices a base
 * and a per_seat for EACH car type, so the thing an operator actually types
 * into sits one level below the member.
 *
 * @param {*} value
 * @returns {boolean}
 */
function isGroup(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * A structured knob's editable leaves: one entry per value an operator types
 * into, each carrying the path that locates it inside the knob's value. A
 * member holding a plain value is one leaf (`['outbound']`); a member holding a
 * group is one leaf per value inside it (`['van', 'base']`).
 *
 * This is what lets the page render, compare and diff a one-level and a
 * two-level knob through the same code — the alternative is a second rendering
 * path that only the per-car-type knob uses, which is where the two would drift.
 *
 * @param {Object} value - a structured knob's value.
 * @returns {Array<{path: Array<string>, value: *}>}
 */
export function structuredLeaves(value) {
  return Object.entries(value || {}).flatMap(([member, memberValue]) => (
    isGroup(memberValue)
      ? Object.entries(memberValue).map(([inner, innerValue]) => ({
        path: [member, inner],
        value: innerValue,
      }))
      : [{ path: [member], value: memberValue }]
  ));
}

/**
 * The value at `path` inside a structured knob's value, or undefined when the
 * path does not lead anywhere — which is what a knob gains a car type, or an
 * operator's stored override predates one, looks like.
 *
 * @param {Object} value
 * @param {Array<string>} path
 * @returns {*}
 */
export function leafAt(value, path) {
  return path.reduce((carried, step) => (carried == null ? undefined : carried[step]), value);
}

/**
 * A copy of `value` with the leaf at `path` replaced. Members off the path keep
 * their identity, so a caller comparing members by reference still sees the
 * untouched ones as untouched — which is what stops one edited car type from
 * reporting all five as changed.
 *
 * @param {Object} value
 * @param {Array<string>} path
 * @param {*} next
 * @returns {Object}
 */
export function replaceLeaf(value, path, next) {
  const [step, ...rest] = path;

  return {
    ...value,
    [step]: rest.length === 0 ? next : replaceLeaf(value[step], rest, next),
  };
}

/**
 * A leaf's caption, e.g. "Outbound", "Per seat", "Van per seat". Only the first
 * word is capitalised: the path reads as one phrase naming a field, not as a
 * title.
 *
 * @param {Array<string>} path
 * @returns {string}
 */
export function leafLabel(path) {
  const words = path.map((step) => step.replaceAll('_', ' ')).join(' ');

  return `${words.charAt(0).toUpperCase()}${words.slice(1)}`;
}

/**
 * Parses a duration string produced by {@link formatDuration} back into
 * seconds.
 *
 * @param {string} text - The formatted duration, e.g. "1h 35m" or "5m 00s".
 * @returns {?number} The duration in seconds, or null if the text does not
 *   match either recognized format.
 */
export function parseDuration(text) {
  const hoursMinutes = /^(\d+)h\s+(\d+)m$/.exec(text);
  if (hoursMinutes) {
    const [, hours, minutes] = hoursMinutes;
    return Number.parseInt(hours, 10) * 3600 + Number.parseInt(minutes, 10) * 60;
  }

  const minutesSeconds = /^(\d+)m\s+(\d+)s$/.exec(text);
  if (minutesSeconds) {
    const [, minutes, seconds] = minutesSeconds;
    return Number.parseInt(minutes, 10) * 60 + Number.parseInt(seconds, 10);
  }

  return null;
}

// The mockup writes a decrease with a true minus sign (U+2212), not a hyphen:
// "base −20". At the sizes this line renders, a hyphen reads as punctuation.
const MINUS = '−';

// Below this, a difference is floating-point dust rather than a deviation an
// operator made. Vehicle costs are the only fractional knob and are quoted to
// two decimals ("0.2"), so a millionth is far below anything real.
const NUMERIC_EPSILON = 1e-6;

/**
 * Formats a difference between two durations as a signed, compact delta —
 * the "+25m" half of the comparison line "Global 1h 35m · outbound +25m".
 * Unlike {@link formatDuration} this drops the units it does not need, because
 * the line reports the SIZE of a deviation rather than a duration in its own
 * right.
 *
 * @param {number} deltaSeconds - The signed difference in seconds.
 * @returns {?string} The signed delta, e.g. "+25m" or "−1h 30m", or null when
 *   there is no difference to report.
 */
export function formatDurationDelta(deltaSeconds) {
  if (!deltaSeconds) return null;

  const magnitude = Math.abs(deltaSeconds);
  const parts = [
    [Math.floor(magnitude / 3600), 'h'],
    [Math.floor((magnitude % 3600) / 60), 'm'],
    [Math.round(magnitude % 60), 's'],
  ].filter(([amount]) => amount > 0).map(([amount, unit]) => `${amount}${unit}`);

  return `${deltaSeconds > 0 ? '+' : MINUS}${parts.join(' ')}`;
}

/**
 * Formats a difference between two plain numbers as a signed delta — the
 * "base −20" half of the comparison line "Global 100 · base −20". Fractional
 * differences are kept; floating-point dust is not reported as a difference at
 * all.
 *
 * @param {number} delta - The signed difference.
 * @returns {?string} The signed delta, e.g. "+5" or "−20", or null when there
 *   is no difference to report.
 */
export function formatNumberDelta(delta) {
  if (!delta || Math.abs(delta) < NUMERIC_EPSILON) return null;

  const magnitude = Math.round(Math.abs(delta) * 1e6) / 1e6;
  return `${delta > 0 ? '+' : MINUS}${magnitude}`;
}

// Matches only the exact single-value weekly shape this page can render:
// "minute hour * * day-of-week", with day-of-month and month pinned to "*"
// and every field a single plain number. Anything else — ranges ("9-17"),
// lists ("1,3,5"), steps ("*/15"), or a non-wildcard day-of-month/month —
// falls through to null rather than being silently truncated into a
// plausible-looking but wrong summary.
const WEEKLY_CRON = /^(\d{1,2}) (\d{1,2}) \* \* ([0-6])$/;

/**
 * Formats a weekly cron expression ("minute hour day month day-of-week")
 * as a human-readable schedule, e.g. "Sundays, 04:00". A blank expression
 * means "manual only" — a legitimate state, not an error — and returns null.
 * Any expression that isn't the exact single-value weekly shape (a range,
 * list, step, or a constrained day-of-month/month) also returns null so the
 * caller can fall back to showing the raw expression instead of a fabricated
 * summary.
 *
 * @param {string} expression - The cron expression.
 * @returns {?string} The formatted schedule, or null when the expression is
 *   blank or is not the exact single-value weekly day-of-week shape.
 */
// The every-day counterpart to WEEKLY_CRON above, which cronCadence reuses for
// the weekly case rather than declaring a second copy of the same shape.
// Display only — nothing here changes what is stored or handed to sidekiq-cron.
//
// Captured rather than only tested, so formatCron can name the time of a daily
// run and cronCadence can still ask the cheaper question of the same shape —
// one definition of "daily", so the two can never disagree about what one is.
const DAILY_TIME = /^(\d{1,2}) (\d{1,2}) \* \* \*$/;

/**
 * Zero-pads an hour and minute into a 24-hour clock time.
 *
 * @param {string} hour
 * @param {string} minute
 * @returns {string} e.g. "04:00".
 */
function clockTime(hour, minute) {
  return `${String(Number.parseInt(hour, 10)).padStart(2, '0')}:${String(Number.parseInt(minute, 10)).padStart(2, '0')}`;
}

/**
 * How often a cron fires, as a word, for display beside a next-run time. A
 * next occurrence alone cannot distinguish "runs daily, next on Monday" from
 * "runs Mondays" — both read as "Mon 04:00".
 *
 * Returns null for any shape it cannot name with certainty (ranges, lists,
 * steps, a constrained day-of-month or month), so the caller shows the time
 * without a cadence rather than asserting a wrong one.
 *
 * @param {string} expression - The cron expression.
 * @returns {?string} "daily", "weekly", or null.
 */
export function cronCadence(expression) {
  if (!expression || !expression.trim()) return null;

  const normalized = expression.trim().replace(/\s+/g, ' ');
  if (DAILY_TIME.test(normalized)) return 'daily';
  if (WEEKLY_CRON.test(normalized)) return 'weekly';

  return null;
}

export function formatCron(expression) {
  if (!expression || !expression.trim()) return null;

  const normalized = expression.trim().replace(/\s+/g, ' ');

  const daily = DAILY_TIME.exec(normalized);
  if (daily) {
    const [, minute, hour] = daily;
    return `Every day, ${clockTime(hour, minute)}`;
  }

  const match = WEEKLY_CRON.exec(normalized);
  if (!match) return null;

  const [, minute, hour, dayOfWeek] = match;
  return `${DAY_NAMES[Number.parseInt(dayOfWeek, 10)]}, ${clockTime(hour, minute)}`;
}
